import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import {
    IActivityManagementWorkflow,
    ICheckinPendencies,
} from "../../../../../context/interfaces/IWorkflowContext";
import DialogMessageProps from "../../../../../model/DialogMessageProps";
import { IContent } from "../../../interface/IActivitiesManagementModal";
import TableStyles from "../../../../../styles/components/TableStyles";
import CheckRadioStyles from "../../../../../styles/components/BlueCheckRadioStyles";
import DialogStyles from "../../../../../styles/components/DialogStyles";
import ButtonStyles from "../../../../../styles/components/ButtonStyles";
import ActivitiesManagementFormsStyles from "../../../../../styles/pages/Workflows/ActivitiesManagementFormsStyles";
import PaginationLocal from "../../../../../components/PaginationLocal";
import DialogMessage from "../../../../../components/DialogMessage";

import { useWorkflow } from "../../../../../context/WorkflowContext";
import Service from "../../../../../service/Docente/ActivityManagementWorkflowService";
import GenericUtils from "../../../../../utils/GenericUtils";
import StringUtils from "../../../../../utils/types/StringUtils";
import {
    StatusCheckinEnum,
    StatusCheckinEnumLabel,
} from "../../../../../model/Enums/StatusCheckinEnum";

interface IModalProps {
    workflow: IActivityManagementWorkflow | null;
    handleChangeContent: (content: IContent) => void;
    onClose: (callback?: any) => void;
}

const { StyledContainerButton } = DialogStyles;
const { BtnPrimary } = ButtonStyles;

const ActivitiesManagementForms: React.FC<IModalProps> = ({
    workflow,
    handleChangeContent,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onClose,
}) => {
    const [checkinPendencies, setCheckinPendencies] = useState<
        ICheckinPendencies[]
    >([]);
    const [filteredCheckinPendencies, setFilteredCheckinPendencies] =
        useState<ICheckinPendencies[]>(checkinPendencies);
    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const { instance } = useMsal();
    const { workflowFilter } = useWorkflow();

    const { isDac } = GenericUtils.getUser();

    const AGUARDANDO_CHECKIN = StatusCheckinEnum.AguardandoCheckin;
    const DEFERIR = StatusCheckinEnumLabel.get(
        isDac
            ? StatusCheckinEnum.DeferidoPelaAdministracao
            : StatusCheckinEnum.DeferidoPeloCordenador
    ) as string;
    const INDEFERIR = StatusCheckinEnumLabel.get(
        isDac
            ? StatusCheckinEnum.IndeferidoPelaAdministracao
            : StatusCheckinEnum.IndeferidoPeloCordenador
    ) as string;
    const AGUARDANDO_APROVACAO = "AGUARDANDO_APROVACAO";

    const isReadOnly = () => {
        return workflowFilter.status !== AGUARDANDO_APROVACAO;
    };

    const includeNewFields = (items: ICheckinPendencies[]) => {
        if (!items.length) return [];
        if (isReadOnly()) return items;

        return items.map((item) => {
            return {
                ...item,
                justificativa: "",
                novoStatusCheckin: AGUARDANDO_CHECKIN,
            };
        });
    };

    const handleCloseDialogMessage = () => {
        setDialogMessageProps({} as DialogMessageProps);
    };

    const handleCloseDialogMessageSuccess = () => {
        handleChangeContent("SUMMARY");
        handleCloseDialogMessage();
    };

    const sortCheckinPendenciesByDate = (checkins: ICheckinPendencies[]) => {
        return checkins.sort(
            (a, b) =>
                new Date(a.cartaoAgenda.data).getTime() -
                new Date(b.cartaoAgenda.data).getTime()
        );
    };

    const handleCheckinPendencies = async () => {
        if (!checkinPendencies.length) {
            const response = await Service.getCheckinPendenciesWorkflow(
                workflowFilter.workflowTypeId,
                workflow?.processoId,
                instance,
                isReadOnly()
            );
            if (GenericUtils.isSuccessStatus(response)) {
                if (response?.data?.items.length) {
                    const checkins = includeNewFields(response?.data?.items);

                    const orderedCheckins =
                        sortCheckinPendenciesByDate(checkins);

                    setCheckinPendencies(orderedCheckins);
                } else {
                    // eslint-disable-next-line no-lonely-if
                    if (!isReadOnly()) handleChangeContent("SUMMARY");
                }
            } else {
                const dialogError = {
                    ...GenericUtils.getPropsDialogError(response),
                    onClose: handleCloseDialogMessage,
                };
                setDialogMessageProps(dialogError);
            }
        }
    };

    const handleChangeDefer = (id: number, value: string) => {
        const newCheckinPendencies = checkinPendencies.map((card) => {
            return card.gestaoAtividadeWorkflowItemId === id
                ? { ...card, novoStatusCheckin: value }
                : { ...card };
        });

        setCheckinPendencies(newCheckinPendencies);
    };

    const handleChangeJustification = (id: number, value: string) => {
        const newCheckinPendencies = checkinPendencies.map((card) => {
            return card.gestaoAtividadeWorkflowItemId === id
                ? { ...card, justificativa: value }
                : { ...card };
        });

        setCheckinPendencies(newCheckinPendencies);
    };

    const isEnabledSubmit = () => {
        if (!checkinPendencies || !checkinPendencies.length) return false;

        return !checkinPendencies.filter(
            (card) =>
                !(card.justificativa && card.justificativa?.length >= 10) ||
                (card.novoStatusCheckin &&
                    card.novoStatusCheckin === AGUARDANDO_CHECKIN)
        ).length;
    };

    const handleSubmit = async () => {
        if (isEnabledSubmit()) {
            const response = await Service.updateCheckinPendencies(
                workflowFilter.workflowTypeId,
                workflow?.processoId,
                checkinPendencies,
                instance
            );

            if (GenericUtils.isSuccessStatus(response)) {
                setDialogMessageProps({
                    show: true,
                    type: "success",
                    title: "Operação realizada com sucesso",
                    onClose: handleCloseDialogMessageSuccess,
                });
            } else {
                const dialogError = {
                    ...GenericUtils.getPropsDialogError(response),
                    onClose: handleCloseDialogMessage,
                };
                setDialogMessageProps(dialogError);
            }
        }
    };

    const getSubtitleModal = () => {
        if (isReadOnly())
            return "Confira abaixo os check-ins deferidos ou indeferidos.";

        return "Abaixo você pode deferir ou indeferir os check-ins não realizados.";
    };

    const renameStatus = (status: string) => {
        if (status === StatusCheckinEnumLabel.get(StatusCheckinEnum.Dispensado))
            return "Pendência de Justificativa";
        if (
            status ===
            StatusCheckinEnumLabel.get(StatusCheckinEnum.AguardandoCheckin)
        )
            return "Check-in Não Realizado";

        return status;
    };

    const getJustificationTitle = () => {
        let justification = "Justificativa";

        if (!isReadOnly())
            justification +=
                " obrigatória (máx. 300 caracteres e mín. 10 caracteres)";

        return justification;
    };

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={dialogMessageProps.onClose}
        />
    );

    useEffect(() => {
        handleCheckinPendencies();
    }, []);

    const listCheckinPendencies =
        filteredCheckinPendencies && filteredCheckinPendencies.length ? (
            filteredCheckinPendencies?.map((cards) => (
                <tr key={cards.gestaoAtividadeWorkflowItemId}>
                    <td>{cards.tipoAtividade}</td>
                    <td>{cards.nomeAtividade}</td>
                    <td>
                        {StringUtils.formatDateString(cards.cartaoAgenda.data)}
                    </td>
                    <td>{`${cards.cartaoAgenda?.horaInicio} - ${cards.cartaoAgenda?.horaFim}`}</td>
                    <td>{renameStatus(cards.cartaoAgenda?.statusCheckin)}</td>
                    {!isReadOnly() && (
                        <td>
                            <CheckRadioStyles.StyledFormCheck
                                inline
                                label="Sim"
                                name={`defer_radio_${cards.gestaoAtividadeWorkflowItemId}`}
                                type="radio"
                                id={`defer_radio_${cards.gestaoAtividadeWorkflowItemId}`}
                                onChange={() =>
                                    handleChangeDefer(
                                        cards.gestaoAtividadeWorkflowItemId,
                                        DEFERIR
                                    )
                                }
                                checked={cards.novoStatusCheckin === DEFERIR}
                            />
                            <CheckRadioStyles.StyledFormCheck
                                inline
                                label="Não"
                                name={`not_defer_radio_${cards.gestaoAtividadeWorkflowItemId}`}
                                type="radio"
                                id={`not_defer_radio_${cards.gestaoAtividadeWorkflowItemId}`}
                                onChange={() =>
                                    handleChangeDefer(
                                        cards.gestaoAtividadeWorkflowItemId,
                                        INDEFERIR
                                    )
                                }
                                checked={cards.novoStatusCheckin === INDEFERIR}
                            />
                        </td>
                    )}
                    <ActivitiesManagementFormsStyles.StyledCellJustification>
                        {!isReadOnly() ? (
                            <textarea
                                value={cards.justificativa}
                                placeholder="Insira sua justificativa"
                                onChange={(event: any) =>
                                    handleChangeJustification(
                                        cards.gestaoAtividadeWorkflowItemId,
                                        event.target.value
                                    )
                                }
                                maxLength={300}
                            />
                        ) : (
                            cards.justificativa
                        )}
                    </ActivitiesManagementFormsStyles.StyledCellJustification>
                </tr>
            ))
        ) : (
            <tr>
                <td colSpan={7}>Nenhum resultado encontrado.</td>
            </tr>
        );

    return (
        <>
            <p style={{ padding: "16px 0 40px 0" }}>{getSubtitleModal()}</p>
            <TableStyles.TablePrimary
                bordered
                striped
                responsive
                style={{ marginBottom: 0 }}
            >
                <thead>
                    <tr>
                        <th>Tipo do Cartão</th>
                        <th>Nome da Atividade</th>
                        <th>Data</th>
                        <th>Alocação</th>
                        <th>Status</th>
                        {!isReadOnly() && <th>Deferir</th>}
                        <th>{getJustificationTitle()}</th>
                    </tr>
                </thead>
                <tbody>{listCheckinPendencies}</tbody>
            </TableStyles.TablePrimary>
            <ActivitiesManagementFormsStyles.StyledPaginationContainer>
                <PaginationLocal
                    items={checkinPendencies}
                    itemsPage={10}
                    onPaginate={(cards) => setFilteredCheckinPendencies(cards)}
                    hidePagination={false}
                />
            </ActivitiesManagementFormsStyles.StyledPaginationContainer>
            {!isReadOnly() && (
                <StyledContainerButton>
                    <BtnPrimary
                        style={{ width: 126, marginTop: 24 }}
                        onClick={() => handleSubmit()}
                        disabled={!isEnabledSubmit()}
                    >
                        Finalizar
                    </BtnPrimary>
                </StyledContainerButton>
            )}
            {dialogMessage}
        </>
    );
};

export default ActivitiesManagementForms;
